import React, { useState } from 'react';
import { getDocs, collection, deleteDoc, addDoc, doc, updateDoc, arrayUnion, setDoc, getDoc } from 'firebase/firestore';
import ConfirmDialog from './ConfirmDialog';
import { firestore } from '../lib/firebaseConfig';

// Generate Random Client Data
const generateRandomClient = () => {
  const firstNames = ['John', 'Jane', 'Michael', 'Anna', 'Robert', 'Emily'];
  const lastNames = ['Smith', 'Johnson', 'Brown', 'Taylor', 'Anderson'];
  const randomFirst = firstNames[Math.floor(Math.random() * firstNames.length)];
  const randomLast = lastNames[Math.floor(Math.random() * lastNames.length)];

  return {
    firstName: randomFirst,
    lastName: randomLast,
    email: `${randomFirst.toLowerCase()}.${randomLast.toLowerCase()}@example.com`,
    phone: `+48 7${Math.floor(100000000 + Math.random() * 89999999)}` // Random Polish phone number
  };
};

// Insert Investment, Flats, and Clients
const insertInvestmentData = async () => {
  try {
    // Step 1: Add the investment (Kamienice Calisia)
    const investmentRef = await addDoc(collection(firestore, 'investments'), {
      name: 'Kamienice Calisia',
      location: 'ul. Złota 20A, Kalisz',
      manager: {
        name: 'Aleksander Dutkiewicz',
        phone: '+48 791 689 248'
      },
      status: 'active',
      totalFlats: 5,
      soldFlats: 3,
      reservedFlats: 1
    });

    console.log(`Investment added with ID: ${investmentRef.id}`);

    // Step 2: Create random clients and store their IDs
    let clientIds = [];
    for (let i = 0; i < 5; i++) {
      const clientData = generateRandomClient();
      const clientRef = doc(collection(firestore, 'customers'));
      await setDoc(clientRef, {
        ...clientData,
        purchasedFlats: []
      });
      clientIds.push(clientRef.id);
      console.log(`Client created: ${clientData.firstName} ${clientData.lastName} (ID: ${clientRef.id})`);
    }

    // Step 3: Add flats under the investment
    const flatsData = [
      { type: 'Residential', size: 75, status: 'sold', clients: [clientIds[0]] },
      { type: 'Residential', size: 60, status: 'sold', clients: [clientIds[1]] },
      { type: 'Residential', size: 45, status: 'available', clients: [] },
      { type: 'Residential', size: 85, status: 'reserved', clients: [clientIds[2]] },
      { type: 'Residential', size: 90, status: 'sold', clients: [clientIds[3], clientIds[4]] }
    ];

    for (const flat of flatsData) {
      const flatRef = await addDoc(collection(firestore, `investments/${investmentRef.id}/flats`), {
        ...flat,
        investmentId: investmentRef.id
      });

      console.log(`Flat added with ID: ${flatRef.id}`);

      // Step 4: Link flats to the clients who own them
      for (const clientId of flat.clients) {
        const clientRef = doc(firestore, `customers/${clientId}`);
        await updateDoc(clientRef, {
          purchasedFlats: arrayUnion({
            investmentId: investmentRef.id,
            flatId: flatRef.id,
            purchaseDate: new Date().toISOString()
          })
        });
        console.log(`Client ${clientId} linked to flat ${flatRef.id}`);
      }
    }

    alert('Kamienice Calisia and associated flats with clients inserted successfully.');
  } catch (error) {
    console.error('Error inserting data:', error);
    alert('Failed to insert investment data.');
  }
};



const getInvestmentDetails = async (specificInvestmentId = null) => {
  try {
    let investmentId;
    let investment;

    if (specificInvestmentId) {
      // Fetch specific investment by ID
      const investmentDocRef = doc(firestore, 'investments', specificInvestmentId);
      const investmentSnap = await getDoc(investmentDocRef);

      if (!investmentSnap.exists()) {
        console.log("Investment not found:", specificInvestmentId);
        return null;
      }

      investmentId = investmentSnap.id;
      investment = investmentSnap.data();
    } else {
      // Fallback: fetch the first investment
      const investmentsSnapshot = await getDocs(collection(firestore, "investments"));
      if (investmentsSnapshot.empty) {
        console.log("No investments found");
        return null;
      }
      const investmentDoc = investmentsSnapshot.docs[0];
      investmentId = investmentDoc.id;
      investment = investmentDoc.data();
    }

    // Fetch flats under this investment
    const flatsSnapshot = await getDocs(collection(firestore, `investments/${investmentId}/flats`));
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    // Process flats with payment status calculation
    const flats = await Promise.all(flatsSnapshot.docs.map(async (flatDoc) => {
      const flatData = flatDoc.data();
      const flatId = flatDoc.id;

      // Only check payments for sold flats
      if (flatData.status === 'sold') {
        try {
          // Fetch payments for this flat
          const paymentsSnapshot = await getDocs(
            collection(firestore, `investments/${investmentId}/flats/${flatId}/payments`)
          );

          let hasWindykacja = false;
          let hasBrakDanych = false;
          let hasPoTerminie = false;
          let hasNiepelnaWplata = false;
          let hasDoZaplaty = false;
          let allPaid = true;

          for (const paymentDoc of paymentsSnapshot.docs) {
            const paymentData = paymentDoc.data();

            // Auto-mark overdue payments as "Po terminie" (based on paymentDueDate - 7 days after stage completion)
            if (
              paymentData.payment_status === 'Do zapłaty' &&
              paymentData.paymentDueDate &&
              paymentData.paymentDueDate < today
            ) {
              await updateDoc(paymentDoc.ref, { payment_status: 'Po terminie' });
              paymentData.payment_status = 'Po terminie';
              console.log(`Payment ${paymentDoc.id} auto-marked as "Po terminie" (payment due: ${paymentData.paymentDueDate})`);
            }

            // Track payment statuses
            if (paymentData.payment_status === 'Windykacja') hasWindykacja = true;
            else if (paymentData.payment_status === 'Brak danych') hasBrakDanych = true;
            else if (paymentData.payment_status === 'Po terminie') hasPoTerminie = true;
            else if (paymentData.payment_status === 'Niepełna wpłata') hasNiepelnaWplata = true;
            else if (paymentData.payment_status === 'Do zapłaty') hasDoZaplaty = true;

            if (paymentData.payment_status !== 'Zapłacono' && paymentData.payment_status !== 'Oczekuje') allPaid = false;
          }

          // Compute amount totals from payments (przed statusem - status musi zgadzac
          // sie z kwota pokazywana w kolumnie "Pozostalo do zaplaty")
          let paidAmountTotal = 0;
          let mandatoryAmountTotal = 0;
          for (const paymentDoc of paymentsSnapshot.docs) {
            const pd = paymentDoc.data();
            mandatoryAmountTotal += pd.amount || 0;
            paidAmountTotal += pd.paidAmount || 0;
          }
          const missingAmountTotal = Math.max(0, mandatoryAmountTotal - paidAmountTotal);
          const leftAmountTotal = Math.max(0, (flatData.an_amount || 0) - paidAmountTotal);

          // Determine flat-level status (worst status takes priority).
          // "Zaplacono" tylko gdy nie zostalo ani grosza. Gdy nie ma nic zaleglego,
          // ale sa jeszcze raty na przyszle etapy (status raty "Oczekuje") - "Na biezaco".
          let newFlatStatus = 'Zapłacono';
          if (hasWindykacja) newFlatStatus = 'Windykacja';
          else if (hasPoTerminie) newFlatStatus = 'Po terminie';
          // "Brak danych" bije kazdy status zaplaty. Lokal bez numeru rachunku
          // nie ma jak dostac dopasowanej wplaty, wiec wyliczenie "Zaplacono"
          // albo "Na biezaco" bylo by zgadywaniem podanym jako fakt. Ustepuje
          // tylko realnym sygnalom zaleglosci powyzej.
          else if (hasBrakDanych) newFlatStatus = 'Brak danych';
          else if (hasNiepelnaWplata) newFlatStatus = 'Niepełna wpłata';
          else if (hasDoZaplaty) newFlatStatus = 'Do zapłaty';
          else if (paymentsSnapshot.size === 0) newFlatStatus = 'Do zapłaty';
          else if (missingAmountTotal > 0.01) newFlatStatus = 'Na bieżąco';
          else if (allPaid) newFlatStatus = 'Zapłacono';

          // Update flat's payment_status if changed
          if (flatData.payment_status !== newFlatStatus) {
            await updateDoc(doc(firestore, `investments/${investmentId}/flats/${flatId}`), {
              payment_status: newFlatStatus
            });
            console.log(`Flat ${flatData.code} status updated to "${newFlatStatus}"`);
          }

          return {
            id: flatId,
            investmentId,
            ...flatData,
            payment_status: newFlatStatus,
            paid_amount: paidAmountTotal,
            mandatory_amount: mandatoryAmountTotal,
            missing_amount: missingAmountTotal,
            left_amount: leftAmountTotal,
          };
        } catch (error) {
          console.error(`Error processing payments for flat ${flatId}:`, error);
        }
      }

      return {
        id: flatId,
        investmentId,
        ...flatData
      };
    }));

    const totalFlats = flats.length;
    const soldFlats = flats.filter(flat => flat.status === "sold").length;
    const reservedFlats = flats.filter(flat => flat.status === "reserved").length;


    // Use stored total_size if available, otherwise calculate from flats
    const totalSize = investment.total_size || flats.reduce((sum, flat) => sum + (flat.size || 0), 0);
    const soldSize = flats
      .filter(flat => flat.status === "sold")
      .reduce((sum, flat) => sum + (flat.size || 0), 0);

    // Count flat types
    const flatTypeCounts = flats.reduce((acc, flat) => {
      acc[flat.type] = (acc[flat.type] || 0) + 1;
      return acc;
    }, {});


    // === NEW: Calculate total income from amount_an ===
    const totalIncome = flats.reduce((sum, flat) => {
      const amount = flat.an_amount;
      return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);

    // === NEW: Calculate income to cost ratio ===
    const totalCost = investment.total_cost || 0;
    const incomeToCostRatio = totalCost > 0 ? totalIncome / totalCost : null;


    // Fetch all clients
    const clientsSnapshot = await getDocs(collection(firestore, "customers"));
    const clients = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Map flats to their clients based on purchasedFlats field
    const enrichedFlats = flats.map(flat => {
      const flatClients = clients.filter(client =>
        client.purchasedFlats?.some(f => f.flatId === flat.id)
      );
      return {
        ...flat,
        clients: flatClients.map(client => ({
          id: client.id,
          name: client.surname ? `${client.name} ${client.surname}` : client.name
        }))
      };
    });

    const result = {
      investmentId,  // Pass the investment ID at top level
      name: investment.name,
      location: investment.location,
      manager: investment.manager.name,
      phone: investment.manager.phone,
      stages_total: investment.stages_total,
      current_stage: investment.current_stage,
      completed: investment.completed,
      totalFlats,
      soldFlats,
      totalSize,  // Include total size in the result
      soldSize,   // Include sold size in the result      
      reservedFlats,
      flatTypeCounts,
      totalIncome,           // Added total income
      incomeToCostRatio,
      flats: enrichedFlats,
      clients,
      totalCost,
    };

    console.log(result);
    return result;
  } catch (error) {
    console.error("Error fetching investment details:", error);
  }
};




const deleteAllData = async () => {
  try {
    const investmentsSnapshot = await getDocs(collection(firestore, "investments"));
    for (const investmentDoc of investmentsSnapshot.docs) {
      const flatsSnapshot = await getDocs(collection(investmentDoc.ref, "flats"));
      for (const flatDoc of flatsSnapshot.docs) {
        await deleteDoc(flatDoc.ref);
      }
      await deleteDoc(investmentDoc.ref);
    }

    const clientsSnapshot = await getDocs(collection(firestore, "clients"));
    for (const clientDoc of clientsSnapshot.docs) {
      await deleteDoc(clientDoc.ref);
    }

    console.log("All investment and client data deleted successfully.");
  } catch (error) {
    console.error("Error deleting data:", error);
  }
};

// Fix payment statuses: reset payments without paymentDueDate from "Po terminie"/"Windykacja" to "Do zapłaty"
const fixPaymentStatuses = async () => {
  try {
    console.log("Starting payment status fix...");
    let paymentsFixed = 0;
    let paymentsChecked = 0;

    const investmentsSnapshot = await getDocs(collection(firestore, "investments"));

    for (const investmentDoc of investmentsSnapshot.docs) {
      const investmentId = investmentDoc.id;
      const flatsSnapshot = await getDocs(collection(firestore, `investments/${investmentId}/flats`));

      for (const flatDoc of flatsSnapshot.docs) {
        const flatId = flatDoc.id;
        const paymentsSnapshot = await getDocs(
          collection(firestore, `investments/${investmentId}/flats/${flatId}/payments`)
        );

        for (const paymentDoc of paymentsSnapshot.docs) {
          const paymentData = paymentDoc.data();
          paymentsChecked++;

          // If payment has no paymentDueDate but is marked as overdue, reset to "Do zapłaty"
          if (
            !paymentData.paymentDueDate &&
            (paymentData.payment_status === 'Po terminie' || paymentData.payment_status === 'Windykacja')
          ) {
            await updateDoc(paymentDoc.ref, { payment_status: 'Do zapłaty' });
            console.log(`Fixed payment ${paymentDoc.id}: "${paymentData.payment_status}" → "Do zapłaty" (no paymentDueDate)`);
            paymentsFixed++;
          }
        }
      }
    }

    console.log(`Payment status fix complete. Checked: ${paymentsChecked}, Fixed: ${paymentsFixed}`);
    alert(`Naprawiono statusy płatności!\n\nSprawdzono: ${paymentsChecked}\nNaprawiono: ${paymentsFixed}`);
    return { checked: paymentsChecked, fixed: paymentsFixed };
  } catch (error) {
    console.error("Error fixing payment statuses:", error);
    alert("Wystąpił błąd podczas naprawiania statusów płatności.");
    throw error;
  }
};

const PopulateDataButtons = () => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleteConfirmOpen(false);
    await deleteAllData();
  };

  return (
    <div>
      <button onClick={insertInvestmentData} style={{ marginRight: '10px' }}>
        Populate Investment Data
      </button>
      <button onClick={fixPaymentStatuses} style={{ marginRight: '10px', backgroundColor: 'orange', color: 'white' }}>
        Fix Payment Statuses
      </button>
      <button onClick={handleDeleteClick} style={{ backgroundColor: 'red', color: 'white' }}>
        Delete All Data
      </button>
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        title="Usuń wszystkie dane"
        message="Czy na pewno chcesz usunąć WSZYSTKIE dane? Ta operacja jest nieodwracalna i usunie wszystkie inwestycje, lokale i klientów."
        confirmText="Usuń wszystko"
        cancelText="Anuluj"
        variant="danger"
      />
    </div>
  );
};

export { getInvestmentDetails, deleteAllData, PopulateDataButtons, insertInvestmentData, fixPaymentStatuses };
